import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import DrawingSearchPanel from "../DesignerSearchPanel";
import DrawingSnGrid from "../DesignerSnGrid";
import DesignerSnResizable from "../DesignerSnResizable";
import { useReducer, useCallback } from "react";
import DesignerFreeForm from "../DesignerFreeForm";

type PanelSizes = Record<string, string | number>;

interface Action {
  type: "SET_SIZE";
  key: string;
  value: string | number;
}

interface Props {
  loadData?: any;
}

const reducer = (state: PanelSizes, action: Action): PanelSizes => {
  switch (action.type) {
    case "SET_SIZE":
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

const usePanelSizes = (
  defaultSizes: PanelSizes
): [
  PanelSizes,
  (
    seq: number,
    size: string | number,
    options?: { skipStorage?: boolean }
  ) => void,
] => {
  const panelKeys = Object.keys(defaultSizes);

  const initialSizes: PanelSizes = {};
  panelKeys.forEach((key) => {
    initialSizes[key] = defaultSizes[key];
  });

  const [state, dispatch] = useReducer(reducer, initialSizes);

  const resize = useCallback(
    (seq: number, size: string | number) => {
      const key = panelKeys[seq - 1];
      if (!key) return;

      dispatch({ type: "SET_SIZE", key, value: size });
    },
    [panelKeys]
  );

  return [state, resize];
};

/**
 * (좌)그리드, (우)프리폼
 */
const TemplateReg4 = forwardRef(({ loadData }: Props, ref) => {
  const gridRef1 = useRef<any>(null);
  const freeformRef = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColumns1] = useState<any[]>([{}]);
  const [freeformControl, setFreeformControl] = useState<any[]>([]);
  const [gridSettings1, setGridSettings1] = useState<any[]>([{}]);
  const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };

  const [sizes, onResizeStop] = usePanelSizes(defaultSize);
  useEffect(() => {
    if (!loadData) {
      setFormData(null);
      setFormControlData([]);
      setColumns1([{}]);
      setFreeformControl([{}]);
    } else {
      setFormData(loadData.formData);
      setFormControlData(loadData.formControlData);
      setColumns1(loadData.grid.grid1);
      setFreeformControl(loadData.form.freeform1);
      if (loadData.gridSettings?.grid1)
        setGridSettings1(loadData.gridSettings.grid1);
    }
  }, [loadData]);

  useImperativeHandle(ref, () => ({
    getSaveData,
  }));

  const getSaveData = async () => {
    const searchformData = await searchRef.current.getFormData();
    const searchControls = await searchRef.current.getControls();
    const gridColumns1 = await gridRef1.current.getColumns();
    const gridSettings1 = await gridRef1.current.getSettings();
    const freeformFormData = await freeformRef.current.getFormData();
    const freeformControls = await freeformRef.current.getControls();

    const columnsInfo1: any[] = gridSettings1.columnsInfo;
    let newDefaultValue1: any = {};

    columnsInfo1.map((col1) => {
      if (searchformData[col1.column_name] !== undefined) {
        newDefaultValue1[col1.column_name] = `searchInfo.${col1.column_name}`;
      }
    });

    const mergedDefaultValue1 = {
      ...gridSettings1.defaultValue,
      ...newDefaultValue1,
    };

    const orderedDefaultValue1: any = {};
    columnsInfo1.forEach((col) => {
      if (mergedDefaultValue1.hasOwnProperty(col.column_name)) {
        orderedDefaultValue1[col.column_name] =
          mergedDefaultValue1[col.column_name];
      }
    });

    const rtn = {
      formData: searchformData,
      formControlData: searchControls,
      grid: { grid1: gridColumns1 },
      form: {
        freeform1: freeformControls,
        freeform1ControlData: freeformFormData,
      },
      gridSettings: {
        grid1: {
          ...gridSettings1,
          defaultValue: orderedDefaultValue1,
        },
      },
    };
    return rtn;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <DrawingSearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></DrawingSearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <DesignerSnResizable
          direction={"width"}
          seq={1}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <DrawingSnGrid
            ref={gridRef1}
            seq={1}
            enableAddRow={true}
            columnConfig={columns1}
            rowData={[]}
            title="타이틀"
            settingsData={gridSettings1}
          />
        </DesignerSnResizable>
        <DesignerSnResizable
          direction={"width"}
          seq={2}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <DesignerFreeForm
            type={"쉐어드폼"}
            ref={freeformRef}
            data={{}}
            controls={freeformControl}
          />
        </DesignerSnResizable>
      </div>
    </div>
  );
});

export default TemplateReg4;
